import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Sparkles, Tag as TagIcon, ChevronRight, Star, LayoutList, CalendarDays, MapPin, User } from 'lucide-react';
import Masonry from 'react-masonry-css';
import api from '../config/api';
import SEO from '../components/SEO';
import ProfileCard, { ProfileCardSkeleton } from '../components/ProfileCard';
import SearchInline from '../components/SearchInline';
import { useCategoriesTree } from '../api/categories';
import { BRAND } from '../config/brand';
import { useEventsList } from '../api/events';
import { usePostsList } from '../api/posts';
import { getImageUrl } from '../utils/imageUtils';
import { deriveCover480UrlFromDesktopCover } from '../utils/coverUtils';
import { buildProfilePath } from '../utils/profileUrl';

const Landing = () => {
  // Categories tree (public)
  const { data: categoriesTreeData, isLoading: catsLoading } = useCategoriesTree();
  const topMainCategories = React.useMemo(() => {
    const roots = Array.isArray(categoriesTreeData?.tree) ? categoriesTreeData.tree : [];
    // IMPORTANT: Keep backend sort order (level ASC, sortOrder ASC, name ASC).
    // Do not resort by profileCount here, otherwise the admin-defined order is lost.
    return roots
      .filter((c) => c && Number(c.level) === 1)
      .slice(0, 6);
  }, [categoriesTreeData?.tree]);

  // Profiles showcase (public)
  // Requirement:
  // - "artistiX рекомендует" should show promoted profiles randomly (stable random per day)
  // - fallback to regular cover-sorted list when no promos exist
  // artistiX рекомендует: Promo-Profile, random, mit Cover
  const { data: promoProfiles, isLoading: promoLoading } = useQuery(
    ['landing:promo-profiles'],
    async () => {
      const promoRes = await api.get('/api/profiles', {
        params: { page: 1, limit: 12, sort: 'cover', promoted_only: 1, random: 1, with_cover_only: 1 }
      });
      const promoList = Array.isArray(promoRes?.data?.profiles) ? promoRes.data.profiles : [];
      return promoList.slice(0, 12);
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // Последние обновления каталога: Nur letzte Profile mit Cover, keine Promo, kein Random
  const { data: profilesData, isLoading: profilesLoading } = useQuery(
    ['landing:profiles'],
    async () => {
      const res = await api.get('/api/profiles', { params: { page: 1, limit: 12, sort: 'cover', with_cover_only: 1 } });
      const list = Array.isArray(res?.data?.profiles) ? res.data.profiles : [];
      return list.slice(0, 12);
    },
    { staleTime: 5 * 60 * 1000 }
  );

  // Популярные теги (алфавит/популярные — что доступно)
  const { data: tagsData, isLoading: tagsLoading } = useQuery(
    ['landing:tags'],
    async () => {
      // Популярные сначала, иначе алфавит — обе возвращают { items: [...] }
      try {
        const popular = await api.get('/api/tags/list', { params: { page: 1, limit: 50, sort: 'popular' } });
        const items = Array.isArray(popular?.data?.items) ? popular.data.items : [];
        if (items.length) return items;
      } catch (_) { }
      try {
        const alpha = await api.get('/api/tags/list', { params: { page: 1, limit: 50, sort: 'alpha' } });
        const items = Array.isArray(alpha?.data?.items) ? alpha.data.items : [];
        if (items.length) return items;
      } catch (_) { }
      return [];
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const profiles = Array.isArray(profilesData) ? profilesData.slice(0, 12) : [];
  const tags = Array.isArray(tagsData) ? tagsData.slice(0, 24) : [];
  const popularTagsFallback = ['дизайн', 'интерьер', '3d', 'архитектура', 'панорама', 'образование', 'технологии', 'природа', 'туризм', 'искусство', 'фотография', 'маркетинг', 'брендинг', 'UX', 'UI', 'идеи'];
  const tagsToRender = tags.length ? tags : popularTagsFallback;

  // Masonry breakpoints (same pattern as Feed/CategoryLanding)
  const breakpointColumnsRecommended = {
    default: 3,
    1400: 3,
    1100: 3,
    700: 2,
    500: 1
  };

  const breakpointColumnsLatest = {
    default: 4,
    1400: 3,
    1100: 3,
    700: 2,
    500: 1
  };

  // Events-Teaser (kommende Events mit quadratischen Bildern)
  const { data: eventsData, isLoading: eventsLoading } = useEventsList({ page: 1, limit: 12 });
  const upcomingEventsRaw = React.useMemo(
    () => (Array.isArray(eventsData?.events) ? eventsData.events : []),
    [eventsData?.events]
  );

  // Posts-Teaser (letzte veröffentlichte Posts)
  const { data: postsData, isLoading: postsLoading } = usePostsList({ page: 1, limit: 12 });
  const recentPostsRaw = React.useMemo(
    () => (Array.isArray(postsData?.posts) ? postsData.posts : []),
    [postsData?.posts]
  );

  // Mix: Events + Posts, sortiert nach Datum, max 4 Items
  const mixedUpdates = React.useMemo(() => {
    const now = Date.now();

    const evItems = upcomingEventsRaw
      .filter((ev) => {
        if (!ev) return false;
        if (!ev.image_url) return false; // Für UX nur Events mit Poster
        if (!ev.start_at) return false;
        const t = new Date(ev.start_at).getTime();
        return Number.isFinite(t) && t >= now; // nur kommende
      })
      .map((ev) => ({ ...ev, _type: 'event', _date: ev.start_at }));

    const postItems = recentPostsRaw
      .filter((p) => {
        if (!p) return false;
        if (!p.published_at) return false;
        // allow posts without image too, but they will look better with one
        return true;
      })
      .map((p) => ({ ...p, _type: 'post', _date: p.published_at }));

    return [...evItems, ...postItems]
      .filter((x) => !!x._date)
      .sort((a, b) => new Date(b._date) - new Date(a._date))
      .slice(0, 4);
  }, [upcomingEventsRaw, recentPostsRaw]);

  // Структурированные данные (ItemList категорий + хлебные крошки)
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: BRAND.titleRu,
    url: `${BRAND.baseUrl}/`,
    inLanguage: 'ru-RU',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Главная', item: `${BRAND.baseUrl}/` },
      ]
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: (topMainCategories.length ? topMainCategories : [
        { id: 'bilder', slug: 'bilder', name: 'Изображения', profileCount: 0 },
        { id: '3d-tours', slug: '3d-tours', name: '3D‑туры', profileCount: 0 }
      ]).map((c, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${BRAND.baseUrl}/category/${encodeURIComponent(String(c.slug || ''))}`,
        name: String(c.name || '')
      }))
    }
  };

  return (
    <>
      <SEO
        title="🎭 Каталог артистов и творческих людей"
        description="Каталог артистов, творческих людей и коллективов. Находите исполнителей по категориям и тегам, смотрите портфолио, видео и слушайте треки."
        url="/"
        type="website"
        canonical={`${BRAND.baseUrl}/`}
        image="/images/news-artistix.jpg"
        structuredData={structuredData}
      />

      <div className="max-w-7xl mx-auto">
        <div className="mein-video">

          <video playsinline="true" autoplay="true" muted loop>
            <source src="/videos/art_small.mp4" type="video/mp4"></source>
          </video>

        </div>
        {/* Hero */}
        <section className="hero-image text-white py-16 relative overflow-hidden">



          <div className="absolute inset-0 overflow-hidden pointer-events-none">

            <img
              src="/images/head-start.jpg"
              alt=""
              aria-hidden="true"
              fetchpriority="high"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover"
            />
            {/* <div className="glass-bg"></div>
            <div className="glass-bg"></div>
            <div className="glass-bg"></div> */}
          </div>
          <span className="bottom-3 right-3 absolute text-white z-10 text-sm">Размещайте рекламу, создавайте эвенты. Удачи!</span>
          <div className="relative z-10 px-4">
            <div className="glass-card p-8 mb-8 max-w-4xl mx-auto">
              <div className="flex items-center mb-4">
                <Sparkles className="w-8 h-8 mr-3 floating" />
                <p className="text-4xl md:text-6xl font-bold">{BRAND.name}</p>
              </div>
              <h1 className="text-lg md:text-xl text-white/90 mb-6">
                {BRAND.claimRu}
              </h1>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* <Link
                  to="/katalog"
                  className="glass-card px-6 py-3 text-lg font-semibold text-white hover:scale-[1.02] transition-transform border-2 border-white inline-flex items-center"
                >
                  Открыть каталог
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Link> */}
                <div className="w-full sm:flex-1">
                  <SearchInline
                    className="w-full"
                    inputClassName="border-2 border-white bg-suche shadow-none"
                    inputTextClassName="text-white placeholder-white/90"
                    iconClassName="text-white"
                    placeholder="Поиск по каталогу…"
                    minChars={1}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Профили Werbung */}
        <section className="px-4 py-10 z-10 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-secondary-900">artisti<span className="text-primary-500">X</span> рекомендует</h2>
            <Link to="/pages/tarif" className="text-primary-700 hover:underline inline-flex items-center">
              продвинуть арт-профиль <Star className="ml-1 w-4 h-4" />
            </Link>
          </div>

          <div className="werbung-card">
            {promoLoading ? (
              <Masonry
                breakpointCols={breakpointColumnsRecommended}
                className="masonry-grid"
                columnClassName="masonry-grid_column"
              >
                {[...Array(6)].map((_, idx) => (
                  <div key={`promo-skel-${idx}`} className="mb-6">
                    <ProfileCardSkeleton variant="promo" />
                  </div>
                ))}
              </Masonry>
            ) : promoProfiles && promoProfiles.length > 0 ? (
              <Masonry
                breakpointCols={breakpointColumnsRecommended}
                className="masonry-grid"
                columnClassName="masonry-grid_column"
              >
                {promoProfiles.slice(0, 6).map((p) => (
                  <div key={p.id} className="mb-6">
                    <ProfileCard
                      profile={{
                        ...p,
                        cover_url_480: p?.cover_url_480 || deriveCover480UrlFromDesktopCover(p?.cover_url)
                      }}
                      preferCover480
                    />
                  </div>
                ))}
              </Masonry>
            ) : (
              <div className="text-secondary-600 p-6">Нет данных для отображения.</div>
            )}
          </div>
        </section>

        <section className="pb-6 landing-welcome z-10 relative">
          <div className="card p-4 landing-welcome__card">
            <h2 className="text-2xl font-semibold text-secondary-900 mb-2">Добро пожаловать на artisti<span className="text-primary-500">X</span>.ru!</h2>
            <p>Мы переехали с домена artNEXX.ru на новый адрес для вашего удобства и улучшения сервиса.
              В данный момент сайт работает в режиме бета — возможны некоторые ошибки и временные неполадки.
              Если вы столкнетесь с проблемами, пожалуйста, <Link to="/pages/kontakt" className="underline hover:text-primary-700">сообщите нам</Link> — ваша обратная связь очень важна для нас!
              После завершения всех доработок сайт перейдет в полноценный, стабильный режим работы.</p>
          </div>
        </section>

        {/* Профили недели */}
        <section className="px-4 py-10 last-profil z-10 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-secondary-900">Последние обновления каталога</h2>
            <Link to="/feed" className="text-primary-700 hover:underline inline-flex items-center">
              Все профили <ChevronRight className="ml-1 w-4 h-4" />
            </Link>
          </div>

          {profilesLoading ? (
            <Masonry
              breakpointCols={breakpointColumnsLatest}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {[...Array(8)].map((_, idx) => (
                <div key={`latest-skel-${idx}`} className="mb-6">
                  <ProfileCardSkeleton />
                </div>
              ))}
            </Masonry>
          ) : profiles.length > 0 ? (
            <Masonry
              breakpointCols={breakpointColumnsLatest}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
            >
              {profiles.map((p) => (
                <div key={p.id} className="mb-6">
                  <ProfileCard
                    profile={{
                      ...p,
                      cover_url_480: p?.cover_url_480 || deriveCover480UrlFromDesktopCover(p?.cover_url)
                    }}
                    preferCover480
                  />
                </div>
              ))}
            </Masonry>
          ) : (
            <div className="text-secondary-600 p-6">Нет данных для отображения.</div>
          )}

        </section>

        {/* Ближайшие события + посты (Teaser unten auf der Landingpage) */}
        <section className="px-4 py-10 z-10 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-secondary-900">События и посты</h2>
            <Link to="/events" className="text-primary-700 hover:underline inline-flex items-center">
              Всё <ChevronRight className="ml-1 w-4 h-4" />
            </Link>
          </div>

          {(eventsLoading || postsLoading) ? (
            <div className="bg-white rounded-2xl shadow-soft ring-1 ring-black/5 p-6 text-secondary-600">
              Загрузка…
            </div>
          ) : mixedUpdates.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mixedUpdates.map((item) => {
                const isEvent = item._type === 'event';
                const href = isEvent ? `/events/${item.id}` : (item.slug ? `/posts/${item.slug}` : '/events?type=posts');

                const ownerProfileUrl = !isEvent
                  ? buildProfilePath({
                    profile: {
                      username_slug: item.owner?.username_slug,
                      username: item.owner?.username,
                      id: item.owner_user_id,
                    },
                    categoryPath: {
                      main: { slug: item.owner?.category?.main_slug },
                      sub: { slug: item.owner?.category?.sub_slug },
                      subSub: { slug: item.owner?.category?.sub_sub_slug },
                    },
                  })
                  : null;

                return (
                  <Link
                    key={`${item._type}-${item.id}`}
                    to={href}
                    className="group bg-white rounded-2xl shadow-soft ring-1 ring-black/5 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition transform flex flex-col"
                  >
                    {/* Quadratisches Bild */}
                    <div className="relative w-full overflow-hidden bg-secondary-100">
                      {item.image_url ? (
                        <img
                          src={getImageUrl(item.image_url)}
                          alt={item.title || (isEvent ? 'Event' : 'Post')}
                          className="w-full aspect-square object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full aspect-square flex items-center justify-center text-secondary-400 text-sm">
                          {isEvent ? 'Event' : 'Post'}
                        </div>
                      )}
                    </div>

                    <div className="p-3 flex flex-col gap-2 flex-1 min-h-[7rem]">
                      <div className="flex items-center gap-1.5 text-xs text-secondary-700">
                        <CalendarDays className="w-3.5 h-3.5 text-primary-600" />
                        <span className="truncate">
                          {item._date ? new Date(item._date).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          }) : ''}
                        </span>
                      </div>

                      <div className="font-semibold text-sm text-secondary-900 line-clamp-2">
                        {item.title || item.body || (isEvent ? 'Событие' : 'Пост')}
                      </div>

                      {isEvent ? (
                        (item.city || item.venue) ? (
                          <div className="flex items-center gap-1.5 text-xs text-secondary-700">
                            <MapPin className="w-3.5 h-3.5" />
                            <span className="truncate">
                              {item.venue && <>{item.venue}, </>}
                              {item.city}
                            </span>
                          </div>
                        ) : null
                      ) : (
                        ownerProfileUrl ? (
                          <div className="flex items-center gap-1.5 text-xs text-secondary-700">
                            <User className="w-3.5 h-3.5" />
                            <span className="truncate">
                              {item.owner?.title || item.owner?.username || ''}
                            </span>
                          </div>
                        ) : null
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-soft ring-1 ring-black/5 p-6 text-secondary-600">
              Пока нет обновлений.
            </div>
          )}
        </section>
        {/* Категории */}
        <section className="px-4 py-10 z-10 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-secondary-900">Категории</h2>
            <Link to="/katalog" className="text-primary-700 hover:underline inline-flex items-center">
              Все категории <ChevronRight className="ml-1 w-4 h-4" />
            </Link>
          </div>

          {catsLoading ? (
            <div className="text-secondary-600 p-6">Загрузка категорий…</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-3">
              {/* 6 Kategorien -> 2 Reihen (3x2) ab md */}
              {(topMainCategories.length ? topMainCategories : []).map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${encodeURIComponent(String(c.slug))}`}
                  className="group relative overflow-hidden rounded-2xl bg-white shadow-soft ring-1 ring-black/5 p-5 hover-blau transition"
                  title={c.name}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/8 to-secondary-500/6 pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <LayoutList className="w-5 h-5 text-primary-500 shrink-0" />
                        <h3 className="text-base font-semibold text-secondary-900 truncate">{c.name}</h3>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary-50 border border-secondary-200 text-secondary-700">
                        {Number(c.profileCount || 0)}
                      </span>
                    </div>

                    <div className="mt-3 inline-flex items-center text-primary-700 font-medium text-sm">
                      Открыть <ChevronRight className="ml-1 w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}

              {!topMainCategories.length && (
                <div className="text-secondary-600 p-6">Нет данных для отображения.</div>
              )}
            </div>
          )}
        </section>

        {/* Популярные теги */}
        <section className="px-4 py-10 z-10 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-secondary-900">Популярные теги</h2>
            <Link to="/tags" className="text-primary-700 hover:underline inline-flex items-center">
              Все теги <ChevronRight className="ml-1 w-4 h-4" />
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-soft ring-1 ring-black/5 p-4">
            {tagsLoading ? (
              <div className="text-secondary-600 p-6">Загрузка тегов…</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tagsToRender.map((t) => {
                  const tag = typeof t === 'string' ? t : (t?.tag || t?.name || '');
                  if (!tag) return null;
                  return (
                    <Link
                      to={`/tag/${encodeURIComponent(tag)}`}
                      key={tag}
                      title={'тег #' + tag}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary-50 hover:bg-secondary-100 border border-secondary-200 text-secondary-800"
                    >
                      <TagIcon className="w-4 h-4 text-secondary-600" />
                      <span>#{tag}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* SEO‑контент (только на главной) */}
        <section className="px-4 py-10 z-10 relative">
          <div className="bg-white rounded-2xl shadow-soft ring-1 ring-black/5 p-6">
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">{BRAND.name} — {BRAND.claimRu}</h2>
            <p className="text-secondary-700 leading-relaxed">
              Добро пожаловать на {BRAND.name} — каталог артистов, творческих людей и коллективов. Здесь вы сможете находить исполнителей по категориям и тегам, смотреть портфолио, видео и слушать треки.
            </p>
            <p className="text-secondary-700 leading-relaxed mt-3">
              Добавляйте профили в избранное, подписывайтесь на авторов и используйте «Ленту», чтобы быстро открывать новых артистов и актуальные обновления.
            </p>
            <p className="text-secondary-700 leading-relaxed mt-3">
              Портал призван помочь своему посетителю получить широкий и качественно сформированный выбор творческих лиц и организаций. Когда Вы устраиваете праздник, день рождения ребенка, свадьбу, корпоративное мероприятие, банкет, холостятскую вечеринку или другое развлекательное шоу, очень важно - найти подходящего артиста. Этот пробел восполняет этот эксклюзивный портал. Мы представляем Вашему вниманию самых интересных, креативных и современных шоу-менов. Ознакомившись с их работами и медиа-материалами (фото, видео), Вы получите исчерпывающее впечатление об артистах и сможете легче выбрать нужного именно Вам шоу-мена или коллектив.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default Landing;
